import { apiClient } from "./client";

export type TransactionStatus =
  | "created"
  | "signed"
  | "completed"
  | "funded"
  | "voided"
  | "fraud"
  | "cancelled"
  | "declined"
  | "failed";

export interface TransactionOfferSchema {
  id: string;
  amount: number;
  type: "FIXED_APR" | "SAC";
  downPayment: number;
  apr: number;
  duration: number;
  monthlyPayment: number;
  financedAmount: number;
  promoDuration?: number | null;
  promoApr?: number | null;
}

export interface TransactionRefundSchema {
  internalId: string;
  externalId: string;
  amount: number;
  type: string;
  createdAt: string;
  status: "pending" | "declined" | "accepted" | "failed";
}

export interface TransactionConfigSchema {
  webhookUrl: string;
  sendWebhooks: boolean;
  availableOffers: TransactionOfferConfigSchema[];
  selectedOffers: TransactionOfferConfigSchema[];
  errorsCount: number;
  errorsType: "TIMEOUT" | "INTERNAL_SERVER_ERROR" | null;
  allowExtraCharges: boolean;
  allowRefunds: boolean;
  allowPartialRefunds: boolean;
  allowVoids: boolean;
  requireSignature: boolean;
  mdaPercentage: number;
  transactionMinAmount: number;
  transactionMaxAmount: number;
  applicationStatus: string;
  transactionStatus: string;
  refundStatus: string;
  applicationAmountAdjustment: number;
  creditLimit: number;
  availableCredit: number;
  allowCrossLocationAccount: boolean;
  allowCrossApplicationAccount: boolean;
  errorsPerformed: number;
}

export interface TransactionOfferConfigSchema {
  type: "FIXED_APR" | "SAC";
  duration: number;
  downPayment: number | null;
  apr: number;
  promoDuration?: number;
  promoApr?: number;
  id?: string;
}

export interface TransactionShortOutputSchema {
  internalId: string;
  externalId: string;
  locationName: string;
  applicationId: string;
  amount: number;
  downPayment: number;
  status: TransactionStatus;
  signUrl: string | null;
  receiptUrl: string | null;
  offer: TransactionOfferSchema | null;
  refundedAmount: number;
  refundCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface TransactionOutputSchema {
  internalId: string;
  externalId: string;
  locationName: string;
  applicationId: string;
  amount: number;
  downPayment: number;
  status: TransactionStatus;
  signUrl: string | null;
  receiptUrl: string | null;
  offer: TransactionOfferSchema | null;
  refundedAmount: number;
  refundCount: number;
  refunds: TransactionRefundSchema[];
  effectiveAt: string | null;
  signedAt: string | null;
  paidAt: string | null;
  paidAmount: number;
  configuration: TransactionConfigSchema;
  createdAt: string;
  updatedAt: string;
}

export interface TransactionsListResponse {
  items: TransactionShortOutputSchema[];
  total: number;
  offset: number;
  limit: number;
}

export interface TransactionCreateSchema {
  externalId: string;
  applicationId: string;
  offerId: string;
  effectiveAt?: string;
  webhookUrl?: string | null;
}

export interface TransactionUpdateSchema {
  amount?: number | null;
  downPayment?: number | null;
  status?: TransactionStatus | null;
  effectiveAt?: string | null;
  webhookUrl?: string | null;
}

export interface TransactionSignSchema {}

export interface TransactionRefundCreateSchema {
  amount: number;
  externalId: string;
}

export interface RefundsListResponse {
  items: TransactionRefundSchema[];
  total: number;
  offset: number;
  limit: number;
}

export interface TransactionConfigUpdateSchema {
  webhookUrl?: string | null;
  sendWebhooks?: boolean | null;
  availableOffers?: TransactionOfferConfigSchema[] | null;
  selectedOffers?: TransactionOfferConfigSchema[] | null;
  errorsCount?: number | null;
  errorsType?: "TIMEOUT" | "INTERNAL_SERVER_ERROR" | null;
  allowExtraCharges?: boolean | null;
  allowRefunds?: boolean | null;
  allowPartialRefunds?: boolean | null;
  allowVoids?: boolean | null;
  requireSignature?: boolean | null;
  mdaPercentage?: number | null;
  transactionMinAmount?: number | null;
  transactionMaxAmount?: number | null;
  applicationStatus?: string | null;
  transactionStatus?: string | null;
  refundStatus?: string | null;
  applicationAmountAdjustment?: number | null;
  creditLimit?: number | null;
  availableCredit?: number | null;
  allowCrossLocationAccount?: boolean | null;
  allowCrossApplicationAccount?: boolean | null;
}

export interface TransactionOfferInputSchema {
  type: "FIXED_APR" | "SAC";
  duration: number;
  downPayment: number | null;
  apr: number;
  promoDuration?: number;
  promoApr?: number;
}

export interface GetTransactionsParams {
  location_name?: string | null;
  status?: TransactionStatus | null;
  offset?: number;
  limit?: number;
}

export interface GetRefundsParams {
  offset?: number;
  limit?: number;
}

export const transactionsApi = {
  /**
   * Get all transactions with pagination and filters
   */
  getTransactions: async (params?: GetTransactionsParams): Promise<TransactionsListResponse> => {
    const response = await apiClient.get("/transactions/", { params });
    return response.data;
  },

  /**
   * Create a new transaction
   */
  createTransaction: async (data: TransactionCreateSchema): Promise<TransactionOutputSchema> => {
    const response = await apiClient.post("/transactions/", data);
    return response.data;
  },

  /**
   * Get a single transaction by ID
   */
  getTransaction: async (transactionId: string): Promise<TransactionOutputSchema> => {
    const response = await apiClient.get(`/transactions/${transactionId}`);
    return response.data;
  },

  /**
   * Update transaction
   */
  updateTransaction: async (
    transactionId: string,
    data: TransactionUpdateSchema
  ): Promise<TransactionOutputSchema> => {
    const response = await apiClient.patch(`/transactions/${transactionId}`, data);
    return response.data;
  },

  /**
   * Sign transaction
   */
  signTransaction: async (transactionId: string): Promise<TransactionOutputSchema> => {
    const response = await apiClient.get(`/transactions/${transactionId}/sign`);
    return response.data;
  },

  /**
   * Reject transaction (delete sign)
   */
  rejectTransaction: async (transactionId: string): Promise<TransactionOutputSchema> => {
    const response = await apiClient.delete(`/transactions/sign/${transactionId}`);
    return response.data;
  },

  /**
   * Create refund for transaction
   */
  createRefund: async (
    transactionId: string,
    data: TransactionRefundCreateSchema
  ): Promise<TransactionRefundSchema> => {
    const response = await apiClient.post(`/transactions/${transactionId}/refunds`, data);
    return response.data;
  },

  /**
   * Get refunds for transaction
   */
  getRefunds: async (
    transactionId: string,
    params?: GetRefundsParams
  ): Promise<RefundsListResponse> => {
    const response = await apiClient.get(`/transactions/${transactionId}/refunds`, { params });
    return response.data;
  },

  /**
   * Update transaction configuration
   */
  updateTransactionConfig: async (
    transactionId: string,
    data: TransactionConfigUpdateSchema
  ): Promise<TransactionOutputSchema> => {
    const response = await apiClient.patch(`/transactions/${transactionId}/config`, data);
    return response.data;
  },

  /**
   * Add offer to transaction
   */
  addOffer: async (
    transactionId: string,
    data: TransactionOfferInputSchema
  ): Promise<TransactionOutputSchema> => {
    const response = await apiClient.post(`/transactions/${transactionId}/offer`, data);
    return response.data;
  },
};
